import React, { useEffect, useState } from 'react';

import { Box, Fade, Typography, Zoom, Slide } from '@mui/material';

import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useInitializationProgress } from 'hooks/useInitializationProgress';

/**
 * Componente centralizado de loading para inicialização
 * Design discreto e focado na experiência do usuário
 */
const InitializationLoader: React.FC = () => {
  const theme = useThemeContext();
  const { overallProgress, isInitializing, initializationStatus, hasInitialized } =
    useInitializationProgress();

  const [showLoader, setShowLoader] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // Controla quando mostrar/esconder o loader
  useEffect(() => {
    if (hasInitialized && !isExiting) {
      // Delay maior para dar tempo de ler as mensagens
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          setShowLoader(false);
        }, 500); // Aumentado para 500ms
      }, 2000); // Aumentado para 2 segundos
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, isExiting]);

  if (!showLoader) {
    return null;
  }

  return (
    <Zoom in={showLoader && !isExiting} timeout={200} appear>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Zoom in={showLoader && !isExiting} timeout={700} appear>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: 400,
              width: '100%',
              px: 4,
            }}
          >
            {/* Logo simplificado */}
            <Slide direction="down" in={showLoader && !isExiting} timeout={800} appear>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '50%',
                    background:
                      'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                    animation: 'shimmer 2s infinite',
                  },
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  },
                }}
              >
                <Typography variant="h3" color="white" fontWeight="bold">
                  A
                </Typography>
              </Box>
            </Slide>

            {/* Mensagem principal */}
            <Fade in={showLoader && !isExiting} timeout={900} appear>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h6" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                  {initializationStatus.message}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {initializationStatus.description}
                </Typography>
              </Box>
            </Fade>

            {/* Barra de progresso discreta */}
            <Fade in={showLoader && !isExiting} timeout={1000} appear>
              <Box sx={{ width: '100%', mb: 2 }}>
                <Box
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'grey.100',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${overallProgress}%`,
                      background: theme.primary,
                      borderRadius: 3,
                      transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 1px 3px rgba(primary, 0.3)',
                      animation:
                        overallProgress > 0 && overallProgress < 100
                          ? 'progressPulse 2s infinite ease-in-out'
                          : 'none',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${overallProgress}%`,
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                      borderRadius: 3,
                      animation:
                        overallProgress > 0 && overallProgress < 100
                          ? 'linearSpin 1.5s infinite linear'
                          : 'none',
                    },
                    '@keyframes progressPulse': {
                      '0%': {
                        opacity: 1,
                        boxShadow: '0 1px 3px rgba(primary, 0.3)',
                      },
                      '50%': {
                        opacity: 0.8,
                        boxShadow: '0 2px 6px rgba(primary, 0.5)',
                      },
                      '100%': {
                        opacity: 1,
                        boxShadow: '0 1px 3px rgba(primary, 0.3)',
                      },
                    },
                    '@keyframes linearSpin': {
                      '0%': {
                        transform: 'translateX(-100%)',
                      },
                      '100%': {
                        transform: 'translateX(100%)',
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    color: overallProgress === 100 ? theme.primary : 'text.secondary',
                    animation: overallProgress === 100 ? 'pulse 0.6s ease-in-out' : 'none',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        color: 'text.secondary',
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        color: theme.primary,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        color: theme.primary,
                      },
                    },
                  }}
                >
                  {overallProgress}%
                </Typography>
              </Box>
            </Fade>

            {/* Indicador de carregamento discreto */}
            {isInitializing && (
              <Fade in={showLoader && !isExiting} timeout={1200} appear>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    '& > div': {
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: theme.primary,
                      animation: 'bounce 1.2s infinite ease-in-out both',
                      '&:nth-of-type(1)': { animationDelay: '-0.24s' },
                      '&:nth-of-type(2)': { animationDelay: '-0.12s' },
                      '&:nth-of-type(3)': { animationDelay: '0s' },
                    },
                    '@keyframes bounce': {
                      '0%, 80%, 100%': {
                        transform: 'scale(0.8)',
                        opacity: 0.5,
                      },
                      '40%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <div />
                  <div />
                  <div />
                </Box>
              </Fade>
            )}
          </Box>
        </Zoom>
      </Box>
    </Zoom>
  );
};

export default InitializationLoader;
